import React, { useEffect, useState } from 'react';
import {Autocomplete, darken, lighten, Stack, TextField, Typography, Button} from '@mui/material';
import Box from '@mui/material/Box';
import api from '../../../services/api';
import CircularProgress from '@mui/material/CircularProgress';
import LargePopper from '../assets/LargePopper';
import {styled} from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import {SlidingPaneAddNew} from "../../Page/SlidingPaneAddNew";

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.primary.main,
  backgroundColor: lighten(theme.palette.primary.light, 0.85),
  ...theme.applyStyles('dark', {
    backgroundColor: darken(theme.palette.primary.main, 0.8),
  }),
}));

const GroupItems = styled('ul')({
  padding: 0,
});


export default function MultiSelectMUI({
  params,
  column,
  editingChip,
}: {
  params: any;
  column: any;
  editingChip?: (value: boolean) => void;
}) {
  const columnId = params.field;

  const [selectOptions, setSelectOptions] = useState<[]>([]);
  const [addingEntity, setAddingEntity] = useState(false);
  const [loading, setLoading] = useState(false);
  const transformedValue = params?.value;

  const fetchSelectOptions = async (
    searchText: string,
    columnName?: string,
    row?: any
  ) => {
    setLoading(true);

    const searchParams = new URLSearchParams({
      searchValue: searchText,
    });

    if (column === 'strippers') {
      searchParams.append(
        'filters',
        JSON.stringify({
          activity_reservation: {
            0: { id: row?.id },
          },
        })
      );
    }

    if (column?.url) {
      api.get(`/${column?.url}/names`, searchParams).then((result: any) => {
        if (result?.data) {
          result.data.unshift({ id: 0, name: '', isButton: true });
          setSelectOptions(result.data);
        } else {
          result.unshift({ id: 0, name: '', isButton: true });
          setSelectOptions(result);
        }
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  };

  const onChange = (event: any, value: any) => {
    let isReturn = false;
    const newValue = value.filter((item: any) => {
      if (item?.isButton) {
        isReturn = true;
      }
      return !item?.isButton;
    });

    params.api.setEditCellValue({
      id: params.id,
      field: params.field,
      value: newValue,
    });

    if (isReturn) {
      setAddingEntity(true);
    }
  };

  const [searchText, setSearchText] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);

  useEffect(() => {
    if (searchText === '') {
      setFilteredOptions(selectOptions); // Ha nincs keresés, minden opció látható
    } else {
      setFilteredOptions(
        selectOptions.filter((option: any) => {
          return option?.name
            ? option.name.toLowerCase().includes(searchText.toLowerCase())
            : false;
        })
      );
    }
  }, [searchText, selectOptions]);

  return (
    <>
      <div className="multiselect-field w-full">
        <Autocomplete
          freeSolo
          onOpen={() => fetchSelectOptions(
            '',
            columnId,
            params?.row
          )}
          sx={{
            '& .MuiOutlinedInput-root': {
              padding: '0',
            },
            '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
              paddingTop: '0px',
              paddingBottom: '0px',
            },
            '& .MuiButtonBase-root-MuiChip-root': {
              borderRadius: '4px',
            },
          }}
          PopperComponent={LargePopper}
          onInputChange={async (event, newInputValue, reason) => {
            if (reason === 'input') {
              setSearchText(newInputValue);
            }
          }}
          multiple
          fullWidth={true}
          options={selectOptions}
          getOptionKey={(option) => option?.id}


          groupBy={(option: any) => {
            let groupName: any = option?.group;

            if (option?.isButton) {
              groupName = null;
            }

            return groupName;
          }}
          renderGroup={(params: any) => {
            return (
              <li key={params.key}>
                {params.group && <GroupHeader>{params.group}</GroupHeader>}
                <GroupItems>{params.children}</GroupItems>
              </li>
            );
          }}


          isOptionEqualToValue={(option, value) => {
            return option.id === value.id;
          }}
          value={transformedValue}
          defaultValue={transformedValue}
          size="small"
          onChange={(event, newValue) => {
            onChange(event, newValue);
          }}
          autoHighlight={false}
          renderOption={(props: any, option: any) => {
            return (
              <Box
                component="li"
                sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                {...props}
              >
                {option?.isButton && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    size={'small'}
                    fullWidth
                  >
                    Add new
                  </Button>
                )}
                <Stack direction={'row'} gap={1}>
                  <Typography>{option.name}</Typography>
                  {option?.address_line1 && (
                    <>
                      <Typography>({option?.address_line1})</Typography>
                    </>
                  )}
                </Stack>
              </Box>
            );
          }}
          getOptionLabel={(option) => {
            let text = option?.name;

            if (!text) {
              if (option?.accommodation) {
                text = option?.accommodation?.name;
              }else{
                text = option?.address_line1;
              }
            }
            return text;
          }}
          renderInput={(paramsForInput) => {
            return (
              <TextField
                {...paramsForInput}
                size="small"
                InputLabelProps={{
                  style: {
                    fontSize: 13,
                    paddingTop: 2,
                  },
                }}
                InputProps={{
                  ...paramsForInput.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress
                          color="inherit"
                          size={20}
                          className={
                            'absolute right-[9px] text-[#b7b7b7] top-[1px]'
                          }
                        />
                      ) : null}
                      {paramsForInput.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            );
          }}
        />
      </div>
      {addingEntity && (
        <SlidingPaneAddNew
          schema={column?.tempSchema}
          id={'new'}
          hasSlidingPane={false}
          onSubmit={(value: any) => {
            const oldValue: any = [...params.value];

            oldValue.map((item: any, index: number) => {
              if (item?.id === value?.id) {
                oldValue[index] = value;
              }
            });

            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: oldValue,
            });

            if (editingChip) {
              editingChip(true);
            }

            setAddingEntity(false);
          }}
          propertiesFromTable={params?.row}
          row={params.row}
          onClose={(value) => {
            setAddingEntity(false);
          }}
        />
      )}
    </>
  );
}
